import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

const axiosInstance = createAxiosInstance(
  config.ADMIN_API_BASE_URL.replace("/admin-api", "/email-api"),
);

export enum EmailEventType {
  RATE_CHANGE = "RATE_CHANGE",
  CREDIT_ALERT = "CREDIT_ALERT",
  DAILY_REPORT = "DAILY_REPORT",
  DEACTIVATION_REPORT = "DEACTIVATION_REPORT",
  USER_CREATE = "USER_CREATE",
  RATE_LOSS = "RATE_LOSS",
  SOA_REPORT = "SOA_REPORT",
  PASSWORD_RESET = "PASSWORD_RESET",
  INVOICE = "INVOICE",
  TWO_FACTOR_AUTH = "TWO_FACTOR_AUTH",
}

// Event types that cannot be resent (contain sensitive one-time data)
export const NON_RESENDABLE_EVENT_TYPES: EmailEventType[] = [
  EmailEventType.PASSWORD_RESET,
  EmailEventType.USER_CREATE,
  EmailEventType.TWO_FACTOR_AUTH,
];

export enum NotificationStatus {
  SENT = "SENT",
  FAILED = "FAILED",
  RETRYING = "RETRYING",
  PARTIALLY_SENT = "PARTIALLY_SENT",
  SENDING = "SENDING",
}

export interface INotification {
  id: string;
  eventType: EmailEventType;
  status: NotificationStatus;
  fromEmail: string;
  sendToEmail: string;
  ccEmail?: string;
  subject: string;
  body: string;
  isEmailSent: boolean;
  failedTries: number;
  sentDate: string;
  attachmentId?: string;
  detailsAttachmentId?: string;
}

export interface NotificationParams {
  page?: number;
  size?: number;
  search?: string;
  sort?: string;
  fromDate?: Date | null;
  toDate?: Date | null;
  eventType?: string;
  status?: string;
}

export async function fetchNotifications(
  params: NotificationParams,
): Promise<IPageable<INotification>> {
  try {
    const queryParams: Record<string, any> = {
      page: params.page ?? 0,
      size: params.size ?? 10,
    };

    if (params.search) {
      queryParams.search = params.search;
    }

    if (params.sort) {
      queryParams.sort = params.sort;
    }

    if (params.fromDate) {
      queryParams.fromDate =
        params.fromDate instanceof Date
          ? params.fromDate.toISOString()
          : params.fromDate;
    }

    if (params.toDate) {
      queryParams.toDate =
        params.toDate instanceof Date
          ? params.toDate.toISOString()
          : params.toDate;
    }

    if (params.eventType) {
      queryParams.eventType = params.eventType;
    }

    if (params.status) {
      queryParams.status = params.status;
    }

    const response = await axiosInstance.get("/notifications", {
      params: queryParams,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching notifications.",
    );
  }
}

export async function fetchNotificationById(
  id: string,
): Promise<INotification> {
  try {
    const response = await axiosInstance.get(`/notifications/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the notification.",
    );
  }
}

export async function resendNotification(id: string): Promise<string> {
  try {
    const response = await axiosInstance.post(`/notifications/${id}/resend`);
    return response.data; // Returns the new notification UUID
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Notification not found.");
    }
    if (error.response?.status === 400) {
      throw new Error(
        error.response?.data?.message ||
          "Cannot resend notifications with sensitive one-time data (Password Reset, User Create, Two-Factor Auth).",
      );
    }
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while resending the notification.",
    );
  }
}

export const exportNotifications = async (
  params: NotificationParams = {},
): Promise<void> => {
  try {
    const queryParams: Record<string, any> = {};

    if (params.search) {
      queryParams.search = params.search;
    }

    if (params.fromDate) {
      queryParams.fromDate =
        params.fromDate instanceof Date
          ? params.fromDate.toISOString()
          : params.fromDate;
    }

    if (params.toDate) {
      queryParams.toDate =
        params.toDate instanceof Date
          ? params.toDate.toISOString()
          : params.toDate;
    }

    if (params.eventType) {
      queryParams.eventType = params.eventType;
    }

    if (params.status) {
      queryParams.status = params.status;
    }

    const response = await axiosInstance.get("/notifications/csv", {
      params: queryParams,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "Notifications.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error exporting notifications:",
      error.response?.data?.message || error.message || error,
    );
    throw new Error(
      error.response?.data?.message || "Failed to export notifications.",
    );
  }
};

export const useNotificationsQuery = (
  params: NotificationParams,
): UseQueryResult<IPageable<INotification>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "sentDate,desc",
  };
  const queryKey = ["notifications", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchNotifications(effectiveParams),
    refetchInterval: (query) => {
      const data = (query as any)?.state?.data as
        | IPageable<INotification>
        | undefined;
      const hasActiveStatus = data?.content?.some(
        (item) =>
          item.status === NotificationStatus.SENDING ||
          item.status === NotificationStatus.RETRYING,
      );
      return hasActiveStatus ? 1000 : false;
    },
  });
};

export const EVENT_TYPE_OPTIONS = [
  { label: "Rate Change", value: EmailEventType.RATE_CHANGE },
  { label: "Credit Alert", value: EmailEventType.CREDIT_ALERT },
  { label: "Daily Report", value: EmailEventType.DAILY_REPORT },
  { label: "Deactivation Report", value: EmailEventType.DEACTIVATION_REPORT },
  { label: "User Create", value: EmailEventType.USER_CREATE },
  { label: "Rate Loss", value: EmailEventType.RATE_LOSS },
  { label: "SOA Report", value: EmailEventType.SOA_REPORT },
  { label: "Password Reset", value: EmailEventType.PASSWORD_RESET },
  { label: "Invoice", value: EmailEventType.INVOICE },
  { label: "Two-Factor Auth", value: EmailEventType.TWO_FACTOR_AUTH },
];

export const STATUS_OPTIONS = [
  { label: "Sent", value: NotificationStatus.SENT },
  { label: "Failed", value: NotificationStatus.FAILED },
  { label: "Retrying", value: NotificationStatus.RETRYING },
  { label: "Partially Sent", value: NotificationStatus.PARTIALLY_SENT },
  { label: "Sending", value: NotificationStatus.SENDING },
];
